import { useMemoizedFn, useUnmount } from "ahooks";
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CanvasType, Plugin, User } from "types";
import shallow from "zustand/shallow";
import { useEmbeddedAppStore } from "./embeddedAppStore";
import * as hostRpc from "./hostRpc";
import { useUser } from "@/hooks/useAccount";
import { getSeller } from "@/utils/getSeller";

// "broken" is not tracked here: it lives only in the store (brokenApps) and is combined with this
// status at the point of display, so the chrome label and canvas body can't drift apart.
type SessionStatus = "loading" | "waking" | "ready";
export type AppStatus = SessionStatus | "broken";

const ESCALATE_AFTER_WAKING_MS = 15_000;
const EMPTY_CONTEXT: Record<string, unknown> = {};

type SessionData = Omit<
    hostRpc.SessionOptions,
    "onHandshakeFailed" | "onReady" | "onWaking" | "onAppBroken" | "onCanvasShow" | "onCanvasHide"
>;

interface BuildSessionDataArguments {
    appId: string;
    origin: string;
    isFirstParty: boolean;
    user: User | undefined;
    route: string;
    context: Record<string, unknown>;
}

export const buildSessionData = ({
    appId,
    origin,
    isFirstParty,
    user,
    route,
    context,
}: BuildSessionDataArguments): SessionData => {
    const seller = getSeller();
    return {
        appId,
        origin,
        isFirstParty,
        sellerId: seller.id,
        userId: user?.id ?? "",
        getCredential: hostRpc.getDefaultCredential,
        roles: user?.roles ?? [],
        locale: user?.locale ?? seller.locale ?? "en-US",
        timezoneName: seller.timezoneName ?? "UTC",
        route,
        context,
    };
};

interface UseEmbeddedAppSessionOptions {
    plugin: Plugin | undefined;
    onHandshakeFailed: () => void;
    onAppBroken: () => void;
    onReload?: () => void;
    onCanvasShow?: hostRpc.SessionOptions["onCanvasShow"];
    onCanvasHide?: hostRpc.SessionOptions["onCanvasHide"];
}

export const useEmbeddedAppSession = ({
    plugin,
    onHandshakeFailed,
    onAppBroken,
    onReload,
    onCanvasShow,
    onCanvasHide,
}: UseEmbeddedAppSessionOptions) => {
    const appId = plugin?.id ?? "";
    const embeddedUrl = plugin?.ui?.embeddedUrl ?? "";
    const canvasType = plugin?.ui?.canvasType ?? CanvasType.DRAWER;
    const isFirstParty = plugin?.ui?.isFirstParty ?? false;
    const origin = useMemo(() => {
        if (!embeddedUrl) {
            return "";
        }
        try {
            return new URL(embeddedUrl).origin;
        } catch {
            return "";
        }
    }, [embeddedUrl]);

    const { isOpen, isDynamicOpen, context } = useEmbeddedAppStore(
        (state) => ({
            isOpen: appId !== "" && state.openCanvases.has(appId),
            isDynamicOpen: appId !== "" && state.dynamicCanvases.has(appId),
            context: (appId !== "" ? state.openCanvases.get(appId) : undefined) ?? EMPTY_CONTEXT,
        }),
        shallow,
    );
    const route = useLocation().pathname;

    // Fullscreen apps are always "open" (they own the whole page)
    const isCanvasOpen =
        canvasType === CanvasType.FULLSCREEN || (canvasType === CanvasType.NONE ? isDynamicOpen : isOpen);

    const user = useUser();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const registeredWindowRef = useRef<Window | null>(null);
    const [status, setStatus] = useState<SessionStatus>("loading");
    const [isLongWaking, setIsLongWaking] = useState(false);

    const handleLoad = useMemoizedFn(() => {
        const win = iframeRef.current?.contentWindow;
        if (!win) {
            return;
        }
        setStatus("loading");
        onReload?.();
        if (registeredWindowRef.current) hostRpc.deregisterSession(registeredWindowRef.current);
        registeredWindowRef.current = win;
        hostRpc.registerSession(win, {
            ...buildSessionData({ appId, origin, isFirstParty, user, route, context }),
            onHandshakeFailed,
            onReady: () => {
                setStatus("ready");
                // A handshake can land after the 60s broken cap (a very slow wake). Clear the
                // broken state too so the placeholder doesn't outlive a now-ready app.
                onReload?.();
            },
            onWaking: () => setStatus("waking"),
            onAppBroken,
            onCanvasShow,
            onCanvasHide,
        });
    });

    // Keep session context current across re-opens without requiring an iframe reload
    useEffect(() => {
        const win = registeredWindowRef.current;
        if (!win) {
            return;
        }
        hostRpc.updateSessionContext(win, context);
    }, [context]);

    // Fire ui.canvas.open on the rising edge of open so the app can refresh on open without
    // following every ui.navigate. Carries the current route + context.
    const wasCanvasOpenRef = useRef(false);
    useEffect(() => {
        const win = registeredWindowRef.current;
        if (win && isCanvasOpen && !wasCanvasOpenRef.current) {
            hostRpc.notifyCanvasOpen(win, route, context);
        }
        wasCanvasOpenRef.current = isCanvasOpen;
    }, [isCanvasOpen, route, context]);

    // Escalate the "waking" copy after a while; reset whenever we leave the waking state.
    useEffect(() => {
        if (status !== "waking") {
            setIsLongWaking(false);
            return;
        }
        const timer = setTimeout(() => setIsLongWaking(true), ESCALATE_AFTER_WAKING_MS);
        return () => clearTimeout(timer);
    }, [status]);

    useUnmount(() => {
        if (registeredWindowRef.current) {
            hostRpc.deregisterSession(registeredWindowRef.current);
            registeredWindowRef.current = null;
        }
    });

    return { iframeRef, handleLoad, status, isLongWaking, isCanvasOpen };
};
